/**
 * Kept for backward compatibility — previously the hardcoded platform floor.
 * No longer unioned into getEffectiveMfaRequiredRoles; the floor is now
 * per-tenant via tenant_mfa_policy.required_roles_floor (read-only from the
 * platform). Exported so existing imports don't break.
 * @deprecated Use the per-tenant floor from useCompanySettings().mfaFloorRoles.
 */
export const FORCED_MFA_ENROLLMENT_ROLES = new Set(['admin']);

/**
 * Merges the per-tenant platform floor with a tenant's configurable policy to
 * produce the effective set of roles that must have TOTP enrolled.
 *
 * Both arguments default to [] so every call-site that omits them fails-open
 * (nothing forced) rather than falling back to the old hardcoded 'admin' floor.
 *
 * @param {string[]|Set<string>|null|undefined} floorRoles
 *   The value of tenant_mfa_policy.required_roles_floor (set by the platform).
 *   Pass null/undefined/[] for "no platform floor".
 * @param {string[]|Set<string>|null|undefined} tenantRequiredRoles
 *   The value of company_settings.mfa_required_roles (tenant self-serve).
 *   Pass null/undefined/[] for "tenant hasn't enabled any extra roles".
 * @returns {Set<string>}
 */
export function getEffectiveMfaRequiredRoles(floorRoles = [], tenantRequiredRoles = []) {
  return new Set([
    ...(floorRoles || []),
    ...(tenantRequiredRoles || []),
  ]);
}

/**
 * @param {string|null|undefined} role  resolved tenant role from useAuth()
 * @param {boolean} hasVerifiedTotp      true if the user has a verified TOTP factor
 * @param {Set<string>} [requiredRoles]  effective required set; defaults to empty (fail-open)
 * @returns {boolean} whether to block this user behind the enrollment gate
 */
export function shouldForceMfaEnrollment(role, hasVerifiedTotp, requiredRoles = new Set()) {
  return requiredRoles.has(role) && !hasVerifiedTotp;
}

/**
 * Returns true when the role's 2FA is enforced (cannot self-serve disable).
 * Pass getEffectiveMfaRequiredRoles(mfaFloorRoles, mfaRequiredRoles) to account
 * for the platform floor and tenant's policy.
 *
 * @param {string|null|undefined} role
 * @param {Set<string>} [requiredRoles]  defaults to empty (fail-open)
 * @returns {boolean}
 */
export function isMfaEnforcedForRole(role, requiredRoles = new Set()) {
  return requiredRoles.has(role);
}
